import * as React from 'react';
import { AlertTriangle, Bug, Home, RotateCw, Send, Check, Compass } from 'lucide-react';
import { cn } from '../../utils';
import { Button } from '../ui/button';

/**
 * Structured payload sent to the backend when a user reports an error.
 * `@rr/ui` only *shapes* this object — the actual network call is supplied by
 * the host app via the `onReport` prop, keeping this package data-fetch-free.
 */
export interface ErrorReport {
  app: string;
  message: string;
  stack?: string;
  statusCode?: number;
  url: string;
  userAgent: string;
  appVersion?: string;
  timestamp: string;
  context?: Record<string, unknown>;
}

export interface ErrorPageProps {
  /** App identifier, used to build the report payload. */
  app: string;
  title?: string;
  description?: string;
  /** The thrown error / route error. */
  error?: unknown;
  /** HTTP-ish status — 404 switches to the "not found" variant. */
  statusCode?: number;
  /** App version string included in the report. */
  appVersion?: string;
  /** Extra context merged into the report (route, userId, …). */
  context?: Record<string, unknown>;
  /** Performs the actual send. Resolve = success, throw = failure. */
  onReport?: (report: ErrorReport) => Promise<void> | void;
  /** Defaults to a full page reload. */
  onRetry?: () => void;
  /** Defaults to "/". */
  homeHref?: string;
  /** i18n overrides for button labels / details toggle. */
  labels?: Partial<{
    retry: string;
    home: string;
    report: string;
    reporting: string;
    reported: string;
    reportFailed: string;
    details: string;
  }>;
  className?: string;
}

function extractError(error: unknown): { message: string; stack?: string } {
  if (error instanceof Error) return { message: error.message, stack: error.stack };
  if (typeof error === 'string') return { message: error };
  if (error && typeof error === 'object') {
    const anyErr = error as Record<string, unknown>;
    const message =
      (typeof anyErr.message === 'string' && anyErr.message) ||
      (typeof anyErr.statusText === 'string' && anyErr.statusText) ||
      JSON.stringify(error);
    return { message, stack: typeof anyErr.stack === 'string' ? anyErr.stack : undefined };
  }
  return { message: 'Unknown error' };
}

type ReportState = 'idle' | 'sending' | 'sent' | 'failed';

export function ErrorPage({
  app,
  title,
  description,
  error,
  statusCode,
  appVersion,
  context,
  onReport,
  onRetry,
  homeHref = '/',
  labels,
  className,
}: ErrorPageProps) {
  const [reportState, setReportState] = React.useState<ReportState>('idle');

  const isNotFound = statusCode === 404;
  const { message, stack } = extractError(error);

  const Icon = isNotFound ? Compass : AlertTriangle;
  const resolvedTitle =
    title ?? (isNotFound ? 'Page not found' : 'Something went wrong');
  const resolvedDescription =
    description ??
    (isNotFound
      ? "The page you're looking for doesn't exist or may have moved."
      : 'An unexpected error occurred. You can retry, or send the details to our team so we can fix it.');

  const l = {
    retry: labels?.retry ?? 'Try again',
    home: labels?.home ?? 'Go home',
    report: labels?.report ?? 'Send report to developer',
    reporting: labels?.reporting ?? 'Sending…',
    reported: labels?.reported ?? 'Report sent',
    reportFailed: labels?.reportFailed ?? 'Send failed — retry',
    details: labels?.details ?? 'Technical details',
  };

  const handleRetry = () => {
    if (onRetry) onRetry();
    else if (typeof window !== 'undefined') window.location.reload();
  };

  const handleReport = async () => {
    if (!onReport || reportState === 'sending' || reportState === 'sent') return;
    setReportState('sending');
    try {
      await onReport({
        app,
        message,
        stack,
        statusCode,
        url: typeof window !== 'undefined' ? window.location.href : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        appVersion,
        timestamp: new Date().toISOString(),
        context,
      });
      setReportState('sent');
    } catch {
      setReportState('failed');
    }
  };

  return (
    <div
      className={cn(
        'min-h-[60vh] w-full flex flex-col items-center justify-center px-6 py-16 text-center',
        className
      )}
    >
      <div className="relative mb-6">
        <Icon className="w-16 h-16 text-foreground opacity-[0.05]" strokeWidth={1} />
        <Icon
          className="absolute inset-0 w-16 h-16 text-muted-foreground/40"
          strokeWidth={1.5}
        />
      </div>

      {statusCode && (
        <p className="text-[12px] font-semibold tracking-wide text-muted-foreground/70 mb-1">
          {statusCode}
        </p>
      )}
      <h1 className="text-[18px] font-semibold text-foreground mb-2">{resolvedTitle}</h1>
      <p className="text-[13px] text-muted-foreground max-w-md leading-relaxed mb-6">
        {resolvedDescription}
      </p>

      <div className="flex flex-wrap items-center justify-center gap-2">
        {!isNotFound && (
          <Button variant="default" leftIcon={RotateCw} onClick={handleRetry}>
            {l.retry}
          </Button>
        )}
        <Button variant="outline" leftIcon={Home} asChild>
          <a href={homeHref}>{l.home}</a>
        </Button>
        {!isNotFound && onReport && (
          <Button
            variant={reportState === 'sent' ? 'success' : 'secondary'}
            leftIcon={
              reportState === 'sent' ? Check : reportState === 'sending' ? undefined : Send
            }
            loading={reportState === 'sending'}
            disabled={reportState === 'sent'}
            onClick={handleReport}
          >
            {reportState === 'sent'
              ? l.reported
              : reportState === 'sending'
                ? l.reporting
                : reportState === 'failed'
                  ? l.reportFailed
                  : l.report}
          </Button>
        )}
      </div>

      {!isNotFound && (message || stack) && (
        <details className="mt-8 w-full max-w-md text-left">
          <summary className="cursor-pointer select-none text-[12px] text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5">
            <Bug className="w-3.5 h-3.5" />
            {l.details}
          </summary>
          <pre className="mt-2 max-h-64 overflow-auto rounded-[12px] border border-border bg-muted/40 p-3 text-[11px] leading-relaxed text-muted-foreground whitespace-pre-wrap break-words">
            {message}
            {stack ? `\n\n${stack}` : ''}
          </pre>
        </details>
      )}
    </div>
  );
}
